import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "reducers";
import { resourcesServices } from "../../services/resource.service";
import { ICharge } from "../../domain/ICharge";
import { IGetChargesRequestParams } from "../../domain/IGetChargesRequestParams";
import { IPopoverState } from "Dashboard/scenes/Carnets/domain/IPopoverState";
import { IUpdateCharge } from "../../domain/IUpdateCharge";
import { IDeleteCharge } from "../../domain/IDeleteCharge";

export const getCharges = createAsyncThunk(
  "Resources/getCharges",
  async (params: IGetChargesRequestParams) => {
    try {
      return await resourcesServices.getCharges(params);
    } catch (err) {
      setError(err);
    }
  }
);

export const updateCharge = createAsyncThunk(
  "ConcreteBase/updateCharge",
  async (data: IUpdateCharge, { rejectWithValue }) => {
    try {
      return await resourcesServices.updateCharge(data);
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

export const deleteCharge = createAsyncThunk(
  "Resources/deleteCharge",
  async (data: IDeleteCharge, { rejectWithValue }) => {
    try {
      await resourcesServices.deleteCharge(data);
      return data;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

interface chargesState {
  loading: boolean;
  dateFrom: Date | null;
  dateTo: Date | null;
  charges: ICharge[];
  totalKilometers: number;
  totalHours: number;
  totalLiters: number;
  discharging?: ICharge;
  updating?: ICharge;
  chargeToDelete?: ICharge;
  chargingResourcePopover: IPopoverState;
  updateChargePopover: IPopoverState;
  dischargingResourcePopover: IPopoverState;
  error: any;
}

const initialState: chargesState = {
  loading: false,
  dateFrom: null,
  dateTo: null,
  charges: [],
  discharging: undefined,
  updating: undefined,
  chargeToDelete: undefined,
  totalKilometers: 0,
  totalHours: 0,
  totalLiters: 0,
  chargingResourcePopover: { isOpen: false },
  updateChargePopover: { isOpen: false },
  dischargingResourcePopover: { isOpen: false },
  error: null,
};

export const chargesSlice = createSlice({
  name: "resources-charges",
  initialState: initialState,
  reducers: {
    setError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    setDateRange: (state, action: PayloadAction<{ dateFrom: Date | null; dateTo: Date | null }>) => {
      state.dateFrom = action.payload.dateFrom;
      state.dateTo = action.payload.dateTo;
    },
    setDischarging: (state, action: PayloadAction<ICharge>) => {
      state.discharging = action.payload;
    },
    setUpdating: (state, action: PayloadAction<ICharge>) => {
      state.updating = action.payload;
    },
    requestDeletingCharge: (state, action: PayloadAction<ICharge | undefined>) => {
      state.chargeToDelete = action.payload;
    },
    setResourceChargePopoverState: (state, action: PayloadAction<IPopoverState>) => {
      state.chargingResourcePopover = action.payload;
    },
    setResourceUpdateChargePopoverState: (state, action: PayloadAction<IPopoverState>) => {
      state.updateChargePopover = action.payload;
    },
    setResourceDischargePopover: (state, action: PayloadAction<IPopoverState>) => {
      state.dischargingResourcePopover = action.payload;
    },
    updateChargeTemporarily: (state, action: PayloadAction<IUpdateCharge>) => {
      for (let chargeIndex = 0; chargeIndex < state.charges.length; chargeIndex++) {
        if (state.charges[chargeIndex].chargeId === action.payload.chargeId) {
          if (action.payload.note !== undefined) {
            state.charges[chargeIndex].note = action.payload.note;
          }
          if (action.payload.quantityAmount !== undefined) {
            state.charges[chargeIndex].quantityAmount = action.payload.quantityAmount;
          }
          if (action.payload.chargeReportSequence !== undefined) {
            state.charges[chargeIndex].chargeReportSequence = action.payload.chargeReportSequence;
          }
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCharges.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getCharges.fulfilled, (state, action) => {
      state.loading = false;
      state.charges = action.payload?.charges ? action.payload.charges : [];
      state.totalKilometers = action.payload?.totalKilometers ? action.payload.totalKilometers : 0;
      state.totalHours = action.payload?.totalHours ? action.payload.totalHours : 0;
      state.totalLiters = action.payload?.totalLiters ? action.payload.totalLiters : 0;
    });
    builder.addCase(getCharges.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
    builder.addCase(updateCharge.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateCharge.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        state.charges = state.charges.map(oldCharge => {
          if (oldCharge.chargeId === action.payload.charge.chargeId) {
            return action.payload.charge;
          } else {
            return oldCharge;
          }
        });
      }
    });
    builder.addCase(updateCharge.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
    builder.addCase(deleteCharge.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteCharge.fulfilled, (state, action) => {
      state.loading = false;
      state.charges = state.charges.filter(charge => charge.chargeId !== action.payload.chargeId);
    });
    builder.addCase(deleteCharge.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
  },
});

export const {
  setError,
  setDateRange,
  setDischarging,
  setUpdating,
  requestDeletingCharge,
  setResourceChargePopoverState,
  setResourceUpdateChargePopoverState,
  setResourceDischargePopover,
  updateChargeTemporarily,
} = chargesSlice.actions;

export const selectState = (state: RootState) => state.resources.charges;
export default chargesSlice.reducer;
